import React from "react";
import { useTranslation } from "react-i18next";
import { InputRow, SelectRow, DetailRow, RadioRow } from "../Rows";
import { FormCard, FormGrid } from "../FormCard";
import {
    OCPP_VERSION_OPTIONS,
    generateBasicAuthPassword,
    isOcpp201,
    toBasicAuthPasswordInput,
} from "./ocppPointHelpers";

const PointForm = ({
    chargingPoint = {},
    handleInputChange,
    chargingSites = [],
    invalidFields = [],
    vendors = [],
}) => {
    const { t } = useTranslation();
    const vendorOptions = vendors.map((vendor) => ({
        value: vendor.manufacturerPublicId,
        label: vendor.manufacturerName,
    }));

    const chargingSitesOptions = chargingSites.map((ch) => ({
        value: ch.chargingSitePublicId,
        label: ch.name,
    }));

    const handleOcppVersionChange = (v) => {
        if (!v) {
            return;
        }
        const switchingTo201 = isOcpp201(v) && !isOcpp201(chargingPoint.ocppVersion);
        handleInputChange("ocppVersion", v);
        if (switchingTo201) {
            handleInputChange("basicAuthPassword", generateBasicAuthPassword());
        } else if (!isOcpp201(v)) {
            handleInputChange("basicAuthPassword", "");
        }
    };

    return (
        <FormCard title={t("form.pointDetails")} className="shadow-sm">
            <FormGrid>
                <InputRow
                    label={t("common.name")}
                    value={chargingPoint.chargingPointName || ""}
                    onChange={(v) => handleInputChange("chargingPointName", v)}
                    invalid={invalidFields?.includes("chargingPointName")}
                    required
                />

                <SelectRow
                    label={t("entity.site")}
                    value={chargingPoint.chargingSitePublicId || ""}
                    options={chargingSitesOptions}
                    onChange={(v) => {
                        const selected = chargingSitesOptions.find((x) => x.value === v);
                        handleInputChange("chargingSitePublicId", v);
                        handleInputChange("chargingSiteName", selected?.label || "");
                    }}
                    required
                    invalid={invalidFields?.includes("chargingSitePublicId")}
                />

                <SelectRow
                    label={t("common.vendor")}
                    value={chargingPoint.chargingPointVendorId || ""}
                    options={vendorOptions}
                    onChange={(v) => {
                        const selected = vendorOptions.find((x) => x.value === v);
                        handleInputChange("chargingPointVendorId", v);
                        handleInputChange("chargingPointVendor", selected?.label || "");
                    }}
                    required
                    invalid={invalidFields?.includes("chargingPointVendor")}
                />

                <InputRow
                    label={t("common.model")}
                    value={chargingPoint.chargingPointModel || ""}
                    onChange={(v) => handleInputChange("chargingPointModel", v)}
                    invalid={invalidFields?.includes("chargingPointModel")}
                    required
                    disabled={false}
                    maxLength={20}
                />

                <SelectRow
                    label={t("form.ocppVersion")}
                    value={chargingPoint.ocppVersion || ""}
                    options={OCPP_VERSION_OPTIONS}
                    onChange={handleOcppVersionChange}
                    required
                    isClearable={false}
                    invalid={invalidFields?.includes("ocppVersion")}
                />

                {isOcpp201(chargingPoint.ocppVersion) && (
                    <InputRow
                        label={t("form.basicAuthPassword")}
                        tooltip={t("point.tooltip.basicAuth")}
                        value={chargingPoint.basicAuthPassword || ""}
                        onChange={(v) =>
                            handleInputChange("basicAuthPassword", toBasicAuthPasswordInput(v))
                        }
                        invalid={invalidFields?.includes("basicAuthPassword")}
                        required
                        maxLength={64}
                    />
                )}

                <DetailRow label={t("common.protocol")} value={chargingPoint.protocolType || ""} />

                <RadioRow
                    label={t("form.multiConnectorPoint")}
                    tooltip={t("point.tooltip.multiConnector")}
                    value={chargingPoint?.isMultiStationChargingPoint}
                    options={[
                        { value: true, label: t("common.yes") },
                        { value: false, label: t("common.no") },
                    ]}
                    onChange={(v) => handleInputChange("isMultiStationChargingPoint", v)}
                    required
                    invalid={invalidFields?.includes("isMultiStationChargingPoint")}
                />
            </FormGrid>
        </FormCard>
    );
};

export default PointForm;
